import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

/**
 * Custom hook to fetch and parse credit agreement concentration limits
 * @param creditFacilityId - The credit facility ID
 * @returns Object containing credit agreement data, loading state, and parsed concentration limits
 */
export const useCreditAgreementConcentrationLimits = (
  creditFacilityId: string,
) => {
  const toast = useToast();

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
  });

  const creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[] = useMemo(() => {
    if (!creditAgreementData?.currentCreditAgreementFullArtifact) {
      return [];
    }

    const creditAgreementString =
      creditAgreementData.currentCreditAgreementFullArtifact.creditAgreement;
    const creditAgreementId =
      creditAgreementData.currentCreditAgreementFullArtifact.id;

    let parsedCreditAgreement = null;
    if (creditAgreementString) {
      try {
        parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreementString,
        );
      } catch (err) {
        Sentry.captureException(err);

        toast({
          ...errorToastConfig,
          id: 'errorGetCurrentCreditAgreementFullArtifact',
          title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
        });
      }
    }

    finleyAssert(
      parsedCreditAgreement,
      `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
    );

    const singleLoanProductConcentrationLimits = parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics.flatMap(
      (loanProductMechanic) => loanProductMechanic.concentrationLimits,
    );
    const multiLoanProductConcentrationLimits =
      parsedCreditAgreement.borrowingBaseMechanics.calcAgentMechanics
        .multiLoanProductConcentrationLimits;

    return [
      ...singleLoanProductConcentrationLimits,
      ...multiLoanProductConcentrationLimits,
    ];
  }, [creditAgreementData, creditFacilityId, toast]);

  return {
    creditAgreementData,
    isCreditAgreementLoading,
    creditAgreementConcentrationLimits,
  };
};
